import {
  useState,
  useCallback,
  useRef,
  useEffect,
  memo,
  type ComponentType,
  type MouseEvent,
} from 'react';
import { IconButton, Tooltip } from '@airbus/components-react';
import TuneIcon from '@mui/icons-material/Tune';
import styles from './GridHeader.module.css';
import { useLanguageContextAPI } from '../../context/LanguageContext';
 
export type ResponsiveActionsProps = {
  isMobile: boolean;
  Actions: ComponentType<any>;
  actionsProps: Record<string, any>;
};
 
const ResponsiveActions = memo(function ResponsiveActions({
  isMobile,
  Actions,
  actionsProps,
}: ResponsiveActionsProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = !!anchorEl;
  const { t } = useLanguageContextAPI();
 
  const handleToggle = useCallback((e: MouseEvent<HTMLElement>) => {
    setAnchorEl((prev) => (prev ? null : e.currentTarget));
  }, []);
 
  const handleClose = useCallback(() => setAnchorEl(null), []);
 
  const ghostRef = useRef<HTMLDivElement | null>(null);
 
  useEffect(() => {
    if (!isMobile || !open) return;
    let attempts = 0;
    const clickWhenReady = () => {
      const btn: HTMLButtonElement | null = ghostRef.current?.querySelector('button');
      if (btn) {
        btn.click();
      } else if (attempts < 5) {
        attempts += 1;
        requestAnimationFrame(clickWhenReady);
      }
    };
    requestAnimationFrame(clickWhenReady);
  }, [isMobile, open]);
 
  if (!isMobile) return <Actions {...actionsProps} />;
 
  return (
    <div className={styles.inlineIconFlexContainer}>
      <Tooltip label={t('actions')}>
        <IconButton
          aria-label='Actions'
          aria-haspopup='menu'
          aria-expanded={open || undefined}
          aria-controls={open ? 'mobile-actions-menu' : undefined}
          data-testid='mobile-actions'
          size='small'
          onClick={handleToggle}
          variant='ghost'
        >
          <TuneIcon sx={{ color: 'rgba(6,59,158,1)' }} />
        </IconButton>
      </Tooltip>
 
      <div ref={ghostRef} className={styles.ghostActionTrigger} aria-hidden>
        {open && (
          <Actions
            {...actionsProps}
            id='mobile-actions-menu'
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
          />
        )}
      </div>
    </div>
  );
});
 
export default ResponsiveActions;
 